export interface ClimateFact {
  label: string;
  value: string;
}

export interface Destination {
  id: string;
  name: string;
  region: string;
  state: string;
  tagline: string;
  summary: string;
  image: string;
  highlights: string[];
  advantages: string[];
  climate: ClimateFact[];
  climateSummary: string;
  climateShort: string;
  elevation: string;
  peakSeason: string;
  primaryMarket: string;
  investment: { title: string; points: string[] }[];
  roi: string;
  startPrice: string;
  focus: string;
}

export const DESTINATIONS: Destination[] = [
  {
    id: "chikmagalur",
    name: "Chikmagalur",
    region: "Western Ghats",
    state: "Karnataka",
    tagline: "The Coffee Land of Karnataka",
    summary:
      "At the foothills of the Mullayanagiri range, Chikmagalur (Chikkamagaluru) is Karnataka's original coffee country. Scattered estates, misty peaks and the highest summit of the state make it a fast-growing premium staycation destination.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20aerial%20view%20of%20Mullayanagiri%20peak%20and%20coffee%20plantations%20in%20Chikmagalur%20Karnataka%2C%20misty%20Western%20Ghats%20mountains%2C%20lush%20green%20terraced%20estates%2C%20golden%20morning%20sunlight%2C%20dramatic%20cloudscape%2C%20real%20landscape%20photography%2C%20ultra%20high%20detail%2C%20no%20buildings&width=1000&height=720&seq=dest-chikmagalur-04&orientation=landscape&nocache=true",
    highlights: [
      "Mullayanagiri — highest peak in Karnataka",
      "Vast coffee & pepper estates",
      "Bababudangiri shrine & misty hills",
      "Hebbe & Kallathigiri waterfalls",
      "Trekking trails and viewpoints",
      "Weekend drive from Bengaluru",
    ],
    advantages: [
      "Growing coffee-estate stay culture with premium nightly rates.",
      "Rising weekend demand from Bengaluru's staycation crowd.",
      "Good road access via Mangaluru and Hassan.",
      "Value entry prices with strong appreciation in estate belts.",
    ],
    climate: [
      { label: "Summer", value: "15°–28°C · Pleasant" },
      { label: "Winter", value: "10°–20°C · Cool" },
      { label: "Monsoon", value: "Jun–Sep · Heavy rain" },
      { label: "Best Time", value: "Oct–May" },
    ],
    climateSummary:
      "A pleasantly cool, aromatic highland climate — fresh coffee-estate mornings and mild evenings that suit year-round villa stays.",
    climateShort: "Cool & aromatic",
    elevation: "~1,090 m",
    peakSeason: "Oct–May",
    primaryMarket: "Bengaluru weekenders",
    investment: [
      {
        title: "Rising staycation demand",
        points: [
          "Coffee-estate and farm-stay trend is expanding fast.",
          "Bengaluru weekend traffic boosts Friday-to-Sunday occupancy.",
          "Premium plantation-stay nightly rates are climbing.",
        ],
      },
      {
        title: "Scenic scarcity",
        points: [
          "Buildable estate parcels in the Ghats are limited.",
          "Protected coffee belts support long-term land value.",
          "Modular A-frame villas suit the rolling estate terrain.",
        ],
      },
      {
        title: "Value entry point",
        points: [
          "Lower entry price than Coorg or Munnar.",
          "Strong upside as the destination builds brand awareness.",
          "Estate ambience commands premium per-night pricing.",
        ],
      },
    ],
    roi: "31%",
    startPrice: "₹74 L",
    focus: "Coffee & Nature Stays",
  },
  {
    id: "coorg",
    name: "Coorg",
    region: "Western Ghats",
    state: "Karnataka",
    tagline: "The Scotland of India",
    summary:
      "Lying at ~900–1,750 m in Karnataka's Western Ghats, Coorg (Kodagu) is a land of rolling coffee and pepper plantations, waterfalls and misty forests. Its cool, aromatic hills are a favourite weekend escape for Bengaluru's traveller community.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20view%20of%20Abbey%20Falls%20waterfall%20in%20Coorg%20Karnataka%20surrounded%20by%20dense%20tropical%20rainforest%2C%20misty%20coffee%20plantations%20in%20background%2C%20fresh%20green%20foliage%2C%20natural%20daylight%2C%20real%20nature%20photography%2C%20ultra%20high%20detail%2C%20no%20buildings&width=1000&height=720&seq=dest-coorg-03&orientation=landscape&nocache=true",
    highlights: [
      "Vast coffee & pepper estates",
      "Abbey Falls & scenic waterfalls",
      "Dubare elephant camp",
      "Raja's Seat viewpoint",
      "Weekend getaway from Bengaluru",
      "Homestay & plantation culture",
    ],
    advantages: [
      "Short drive from Bengaluru — the #1 weekend-drive market.",
      "Exploding plantation-stay and farm-stay trend.",
      "Coffee-estate ambience is highly desirable for villas.",
      "Excellent road access via Mysuru and Madikeri.",
    ],
    climate: [
      { label: "Summer", value: "20°–30°C · Warm" },
      { label: "Winter", value: "12°–22°C · Cool" },
      { label: "Monsoon", value: "Jun–Sep · Heavy rain" },
      { label: "Best Time", value: "Oct–May" },
    ],
    climateSummary:
      "A pleasant, cooler highland climate with aromatic plantation air — fresh mornings and ideal for coffee-estate villa experiences.",
    climateShort: "Pleasant & aromatic",
    elevation: "~900–1,750 m",
    peakSeason: "Oct–May",
    primaryMarket: "Bengaluru weekenders",
    investment: [
      {
        title: "Weekend drive demand",
        points: [
          "Bengaluru is just ~5–6 hours away — huge staycation market.",
          "Consistent Friday-to-Sunday occupancy peaks.",
          "Coorg is the default premium hill-stay pick for South Indians.",
        ],
      },
      {
        title: "Plantation asset appeal",
        points: [
          "Coffee-estate villas blend lifestyle with income.",
          "Plantation parcels have strong long-term land value.",
          "Distinct estate-brand stays attract higher-paying guests.",
        ],
      },
      {
        title: "Operational simplicity",
        points: [
          "Well-established homestay and estate operators to partner with.",
          "Good connectivity and amenities around Madikeri.",
          "Proven appetite for both self-stay and managed rentals.",
        ],
      },
    ],
    roi: "32%",
    startPrice: "₹78 L",
    focus: "Weekend & Estate Stays",
  },
  {
    id: "agumbe",
    name: "Agumbe",
    region: "Western Ghats",
    state: "Karnataka",
    tagline: "The Rainforest of the Ghats",
    summary:
      "Known as the 'Cherrapunji of the South', Agumbe is a misty rainforest hamlet in the Western Ghats famed for its biodiversity, sunsets and waterfall trails. It is a dream canvas for immersive, eco-driven nature stays.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20view%20of%20Agumbe%20rainforest%20sunset%20point%20in%20Karnataka%20Western%20Ghats%2C%20dramatic%20orange%20sunset%20over%20misty%20green%20hills%2C%20dense%20tropical%20canopy%2C%20golden%20hour%20light%2C%20real%20landscape%20photography%2C%20ultra%20high%20detail%2C%20no%20buildings&width=1000&height=720&seq=dest-agumbe-05&orientation=landscape&nocache=true",
    highlights: [
      "UNESCO rainforest biodiversity hotspot",
      "Renowned sunset point over the Ghats",
      "Jogigundi & Onake Abbi waterfalls",
      "Trekking through dense green canopy",
      "Herpetology — the 'King Cobra capital'",
      "Tranquil, low-footfall atmosphere",
    ],
    advantages: [
      "A truly unique eco-tourism story with strong appeal.",
      "Immersive nature stays attract a niche, high-value audience.",
      "Low competition and limited inventory protect occupancy.",
      "Relaxed, conscious guest profile values privacy and quiet.",
    ],
    climate: [
      { label: "Summer", value: "22°–32°C · Humid" },
      { label: "Winter", value: "15°–25°C · Cool" },
      { label: "Monsoon", value: "Jun–Sep · Extremely heavy" },
      { label: "Best Time", value: "Oct–Feb" },
    ],
    climateSummary:
      "One of the wettest places in India — lush rainforest humidity with a crisp dry season, ideal for off-grid, eco-luxury villa retreats.",
    climateShort: "Lush & wet",
    elevation: "~826 m",
    peakSeason: "Oct–Feb",
    primaryMarket: "Eco-travellers & nature lovers",
    investment: [
      {
        title: "Eco-niche appeal",
        points: [
          "Rare rainforest setting commands premium stay rates.",
          "Conscious travellers pay for sustainability and seclusion.",
          "Boutique eco-lodge scarcity keeps demand steady.",
        ],
      },
      {
        title: "Low-competition edge",
        points: [
          "Few organised stays — early movers lead the market.",
          "Distinctive 'rainforest retreat' positioning is hard to copy.",
          "Strong story for social and word-of-mouth marketing.",
        ],
      },
      {
        title: "Affordable entry",
        points: [
          "One of the lowest entry points in our portfolio.",
          "Fits modular, low-impact eco-builds perfectly.",
          "Clear path to solid returns as eco-tourism matures.",
        ],
      },
    ],
    roi: "27%",
    startPrice: "₹58 L",
    focus: "Rainforest & Eco Stays",
  },
  {
    id: "munnar",
    name: "Munnar",
    region: "Western Ghats",
    state: "Kerala",
    tagline: "The Tea Country of Kerala",
    summary:
      "At ~1,600 m in Kerala's Western Ghats, Munnar is a vast carpet of emerald tea estates dotted with misty valleys and spice gardens. Its romantic, lush landscape makes it one of South India's top honeymoon and nature-retreat destinations.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20panoramic%20view%20of%20Munnar%20tea%20plantations%20in%20Kerala%2C%20endless%20rolling%20green%20hills%20with%20neatly%20trimmed%20tea%20bushes%2C%20misty%20valleys%2C%20soft%20morning%20fog%2C%20emerald%20landscape%2C%20real%20aerial%20photography%2C%20ultra%20high%20detail%2C%20no%20buildings&width=1000&height=720&seq=dest-munnar-02&orientation=landscape&nocache=true",
    highlights: [
      "Endless tea & spice plantations",
      "Eravikulam National Park",
      "Mattupetty Dam & boating",
      "Tea museum & factory tours",
      "Honeymoon hotspot",
      "Misty valley viewpoints",
    ],
    advantages: [
      "Massive honeymoon and couples travel demand year-round.",
      "Tea estate aesthetics make every villa a visual asset.",
      "High brand recognition for premium & boutique stays.",
      "Relaxed, nature-driven guest profile = longer stays.",
    ],
    climate: [
      { label: "Summer", value: "15°–25°C · Mild" },
      { label: "Winter", value: "5°–15°C · Chilly" },
      { label: "Monsoon", value: "Jun–Sep · Heavy rain" },
      { label: "Best Time", value: "Sep–Mar" },
    ],
    climateSummary:
      "A cool, moisture-rich climate with misty mornings and lush greenery — a dream setting for romantic, nature-focussed villa stays.",
    climateShort: "Cool & misty",
    elevation: "~1,600 m",
    peakSeason: "Sep–Mar",
    primaryMarket: "Honeymooners & couples",
    investment: [
      {
        title: "Premium nightly rates",
        points: [
          "Honeymooners pay a premium for private, scenic stays.",
          "Boutique A-frame villas command higher ADR than hotels.",
          "Strong seasonal spikes around festive and holiday windows.",
        ],
      },
      {
        title: "Brand & visibility",
        points: [
          "Munnar is a globally known tea-country brand.",
          "Instagram-worthy scenery drives organic booking demand.",
          "Established plantation-tourism ecosystem and operators.",
        ],
      },
      {
        title: "Eco & regulatory edge",
        points: [
          "Clear eco-tourism framework supports responsible builds.",
          "Land scarcity in tea-zone buffers supports appreciation.",
          "Modular prefab villas minimise construction disruption.",
        ],
      },
    ],
    roi: "30%",
    startPrice: "₹92 L",
    focus: "Honeymoon & Boutique",
  },
  {
    id: "wayanad",
    name: "Wayanad",
    region: "Western Ghats",
    state: "Kerala",
    tagline: "The Land of Spice & Forests",
    summary:
      "Nestled on the Kerala–Karnataka border, Wayanad is a lush highland district of spice estates, ancient caves and misty forest reserves. Its wild, green character makes it one of South India's fastest-rising eco-resort destinations.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20view%20of%20Chembra%20Peak%20heart-shaped%20lake%20in%20Wayanad%20Kerala%2C%20lush%20green%20Western%20Ghats%20mountains%2C%20misty%20highland%20scenery%2C%20dramatic%20clouds%2C%20real%20landscape%20photography%2C%20ultra%20high%20detail%2C%20no%20buildings&width=1000&height=720&seq=dest-wayanad-06&orientation=landscape&nocache=true",
    highlights: [
      "Chembra Peak & heart-shaped lake",
      "Meenmutty & Soochipara waterfalls",
      "Edakkal prehistoric caves",
      "Spice & coffee plantations",
      "Wayanad Wildlife Sanctuary",
      "Misty valleys and viewpoints",
    ],
    advantages: [
      "Surge in eco-resort and plantation-stay demand.",
      "Accessible from Bengaluru, Calicut and Mysuru.",
      "Spice-estate land holds strong long-term value.",
      "Lush, cinematic scenery ideal for premium villas.",
    ],
    climate: [
      { label: "Summer", value: "20°–33°C · Warm" },
      { label: "Winter", value: "15°–27°C · Cool" },
      { label: "Monsoon", value: "Jun–Sep · Heavy rain" },
      { label: "Best Time", value: "Sep–Mar" },
    ],
    climateSummary:
      "A warm-to-cool highland climate with dramatic green-season downpours and crisp dry months — great for year-round jungle and spice-estate stays.",
    climateShort: "Warm & lush",
    elevation: "~700–2,100 m",
    peakSeason: "Sep–Mar",
    primaryMarket: "Eco-resort & family travellers",
    investment: [
      {
        title: "Booming eco-resort market",
        points: [
          "Wayanad is one of the fastest-growing nature destinations.",
          "High occupancy around long weekends and festivals.",
          "Strong domestic demand for jungle and estate villas.",
        ],
      },
      {
        title: "Access & connectivity",
        points: [
          "Well-linked from Bengaluru, Mysuru and Calicut.",
          "Growing resort ecosystem supports operations.",
          "Driveable weekend market in addition to fly-in guests.",
        ],
      },
      {
        title: "Land appreciation",
        points: [
          "Spice-estate parcels are scarce and prized.",
          "Protected forest buffers limit new construction.",
          "Modular prefab villas build fast with low site impact.",
        ],
      },
    ],
    roi: "33%",
    startPrice: "₹68 L",
    focus: "Forest & Spice Stays",
  },
  {
    id: "ooty",
    name: "Ooty",
    region: "The Nilgiris",
    state: "Tamil Nadu",
    tagline: "The Queen of Hill Stations",
    summary:
      "Perched at ~2,240 m in the Blue Mountains of the Nilgiri range, Ooty is India's most iconic hill station. Its rolling botanical gardens, eucalyptus-shrouded slopes, and heritage toy train make it a perennial magnet for domestic travellers and an evergreen choice for hospitality investment.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20view%20of%20Ooty%20Nilgiri%20hills%20with%20famous%20toy%20train%20on%20mountain%20railway%20track%2C%20eucalyptus%20trees%2C%20rolling%20green%20meadows%2C%20misty%20valley%20below%2C%20Tamil%20Nadu%20hill%20station%2C%20real%20landscape%20photography%2C%20ultra%20high%20detail&width=1000&height=720&seq=dest-ooty-01&orientation=landscape&nocache=true",
    highlights: [
      "Nilgiri Mountain Railway (UNESCO)",
      "Botanical & Rose Gardens",
      "Doddabetta Peak viewpoints",
      "Ooty Lake & boating",
      "Year-round family crowd",
      "Eco-sensitive zone charm",
    ],
    advantages: [
      "All-season domestic demand — strong school & long-weekend traffic.",
      "Well connected via Coimbatore airport and Nilgiri roads.",
      "Established staycation culture with proven nightly-rate benchmarks.",
      "Cool mountain climate supports cosy villa experiences year-round.",
    ],
    climate: [
      { label: "Summer", value: "15°–28°C · Pleasant" },
      { label: "Winter", value: "5°–15°C · Crisp" },
      { label: "Monsoon", value: "Jun–Sep · Misty rains" },
      { label: "Best Time", value: "Sep–Jun" },
    ],
    climateSummary:
      "A mild, temperate climate with cool summers and crisp winters — ideal for year-round villa stays without extreme heat.",
    climateShort: "Cool year-round",
    elevation: "~2,240 m",
    peakSeason: "Sep–Jun",
    primaryMarket: "Families & schools",
    investment: [
      {
        title: "Proven rental demand",
        points: [
          "Consistent weekend and festival occupancy across the year.",
          "Strong Airbnb & boutique stay nightly-rate benchmarks.",
          "Repeat visitors from metros like Bengaluru and Chennai.",
        ],
      },
      {
        title: "Infrastructure & access",
        points: [
          "Coimbatore international airport ~85 km away.",
          "Improving ghat roads and eco-tourism policies.",
          "Toy train and tourist rail add steady footfall.",
        ],
      },
      {
        title: "Scarcity & appreciation",
        points: [
          "Limited buildable land in eco-sensitive hill zones.",
          "Regulated construction drives long-term land value.",
          "A-frame modular builds suit steep scenic plots well.",
        ],
      },
    ],
    roi: "29%",
    startPrice: "₹84 L",
    focus: "Staycation & Family",
  },
  {
    id: "coonoor",
    name: "Coonoor",
    region: "The Nilgiris",
    state: "Tamil Nadu",
    tagline: "The Tranquil Nilgiri Town",
    summary:
      "A short ride down the Nilgiri Mountain Railway, Coonoor is Ooty's calmer, more elegant neighbour. Tea estates, the heritage toy train and serene viewpoints give it a refined, boutique stay culture without the crowds.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20view%20of%20Coonoor%20tea%20estates%20in%20Nilgiri%20hills%20Tamil%20Nadu%2C%20terraced%20plantations%20on%20steep%20green%20slopes%2C%20misty%20mountain%20atmosphere%2C%20soft%20diffused%20light%2C%20real%20landscape%20photography%2C%20ultra%20high%20detail%2C%20no%20buildings&width=1000&height=720&seq=dest-coonoor-07&orientation=landscape&nocache=true",
    highlights: [
      "Sim's Park botanical garden",
      "Dolphin's Nose viewpoint",
      "Lamb's Rock panoramic vista",
      "Picturesque tea estates",
      "Nilgiri Mountain Railway station",
      "Quieter, boutique alternative to Ooty",
    ],
    advantages: [
      "Less crowded than Ooty yet same iconic hillscape.",
      "Heritage rail and tea estates attract premium guests.",
      "All-season demand with easy access from Coimbatore.",
      "Sought-after boutique and heritage-stay positioning.",
    ],
    climate: [
      { label: "Summer", value: "15°–25°C · Pleasant" },
      { label: "Winter", value: "5°–15°C · Crisp" },
      { label: "Monsoon", value: "Jun–Sep · Misty rains" },
      { label: "Best Time", value: "Sep–Jun" },
    ],
    climateSummary:
      "A mild, temperate Nilgiri climate with crisp mornings and cool evenings — comfortable for cosy villa stays almost all year.",
    climateShort: "Cool year-round",
    elevation: "~1,858 m",
    peakSeason: "Sep–Jun",
    primaryMarket: "Couples & boutique travellers",
    investment: [
      {
        title: "Premium boutique stays",
        points: [
          "Coonoor commands higher per-night rates than crowded Ooty.",
          "Heritage and tea-estate aesthetics justify premium pricing.",
          "Strong repeat visits from discerning South Indian travellers.",
        ],
      },
      {
        title: "Refined positioning",
        points: [
          "Known as the elegant, quieter Nilgiri town.",
          "Toy-train and tea-estate charm drive organic demand.",
          "Ideal for wellness and couples' retreat villas.",
        ],
      },
      {
        title: "Appreciation with Ooty spillover",
        points: [
          "As Ooty tightens, demand flows into Coonoor.",
          "Scenic estate land is scarce and appreciating.",
          "Easy access keeps operational and marketing costs low.",
        ],
      },
    ],
    roi: "30%",
    startPrice: "₹80 L",
    focus: "Quiet & Boutique Stays",
  },
  {
    id: "kotagiri",
    name: "Kotagiri",
    region: "The Nilgiri",
    state: "Tamil Nadu",
    tagline: "The Quietest Nilgiri Hill",
    summary:
      "High in the eastern Nilgiris, Kotagiri is the range's best-kept secret — a peaceful tea-country town with deep shola forests, gentle viewpoints and almost no tourist crowds. An ideal low-key retreat and wellness destination.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20view%20of%20Catherine%20Falls%20waterfall%20in%20Kotagiri%20Nilgiri%20hills%20Tamil%20Nadu%2C%20cascading%20water%20through%20lush%20green%20shola%20forest%2C%20misty%20mountain%20backdrop%2C%20natural%20daylight%2C%20real%20nature%20photography%2C%20ultra%20high%20detail%2C%20no%20buildings&width=1000&height=720&seq=dest-kotagiri-08&orientation=landscape&nocache=true",
    highlights: [
      "Kodanad View Point over the plains",
      "Longwood Shola reserve forest",
      "Catherine Falls & Elk Hill",
      "Vast, serene tea estates",
      "Less commercialised, authentic feel",
      "Perfect for wellness retreats",
    ],
    advantages: [
      "Unspoiled character with rising interest among quiet travellers.",
      "Lower entry price into the premium Nilgiri belt.",
      "Strong fit for retreat, wellness and digital-detox villas.",
      "Proximity to Ooty and Coonoor with far fewer crowds.",
    ],
    climate: [
      { label: "Summer", value: "15°–25°C · Pleasant" },
      { label: "Winter", value: "5°–14°C · Crisp" },
      { label: "Monsoon", value: "Jun–Sep · Misty rains" },
      { label: "Best Time", value: "Sep–May" },
    ],
    climateSummary:
      "A calm, temperate Nilgiri climate with cool misty mornings — ideal for restful, off-the-grid villa retreats.",
    climateShort: "Cool & tranquil",
    elevation: "~1,980 m",
    peakSeason: "Sep–May",
    primaryMarket: "Retreat & wellness seekers",
    investment: [
      {
        title: "Authentic, unspoiled appeal",
        points: [
          "Kotagiri offers genuine, crowd-free tea-country charm.",
          "Wellness and slow-travel demand is rising steadily.",
          "Rare peaceful positioning stands out from crowded hills.",
        ],
      },
      {
        title: "Value into the Nilgiris",
        points: [
          "More affordable land than Ooty or Coonoor.",
          "High-quality scenic estates at a lower entry price.",
          "Clear upside as the destination gains recognition.",
        ],
      },
      {
        title: "Scarcity & appreciation",
        points: [
          "Shola-forest conservation limits buildable land.",
          "Protected ecology supports long-term value growth.",
          "Modular builds fit sensitive, high-altitude plots.",
        ],
      },
    ],
    roi: "29%",
    startPrice: "₹72 L",
    focus: "Tranquil & Retreat Stays",
  },
  {
    id: "mudumalai",
    name: "Mudumalai",
    region: "The Nilgiris",
    state: "Tamil Nadu",
    tagline: "Wildlife Gateway of the Nilgiris",
    summary:
      "Bordering the Mudumalai Tiger Reserve and Bandipur, this wildlife corridor in the Nilgiris is where jungle meets safari. Its wild, untamed edge attracts nature photographers and safari travellers seeking immersive jungle stays.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20view%20of%20Mudumalai%20tiger%20reserve%20in%20Nilgiri%20biosphere%20Tamil%20Nadu%2C%20wild%20elephants%20in%20savanna%20grassland%2C%20teak%20forest%20edge%2C%20golden%20safari%20light%2C%20real%20wildlife%20photography%2C%20ultra%20high%20detail%2C%20no%20buildings&width=1000&height=720&seq=dest-mudumalai-10&orientation=landscape&nocache=true",
    highlights: [
      "Mudumalai Tiger Reserve & safari",
      "Elephant herds and wildlife sightings",
      "Moyar river and gorge",
      "Teak and moist-deciduous forests",
      "Jungle treks and nature trails",
      "Safari circuit with Bandipur & Wayanad",
    ],
    advantages: [
      "Unique wildlife-tourism niche with dedicated demand.",
      "Safari circuit draws steady domestic and nature travellers.",
      "Eco-sensitive stays command premium for seclusion.",
      "Lower entry cost into a high-value natural setting.",
    ],
    climate: [
      { label: "Summer", value: "18°–33°C · Warm" },
      { label: "Winter", value: "12°–25°C · Cool" },
      { label: "Monsoon", value: "Jun–Sep · Moderate" },
      { label: "Best Time", value: "Nov–May" },
    ],
    climateSummary:
      "A warm dry-season climate ideal for safaris, with a cooler, misty winter — well suited to jungle lodge and eco-villa stays.",
    climateShort: "Warm & wild",
    elevation: "~1,000 m",
    peakSeason: "Nov–May",
    primaryMarket: "Wildlife & safari travellers",
    investment: [
      {
        title: "Wildlife niche demand",
        points: [
          "Dedicated safari and nature-photography audience.",
          "Jungle lodges enjoy strong seasonal occupancy.",
          "Part of a famous Bandipur–Mudumalai–Wayanad circuit.",
        ],
      },
      {
        title: "Eco-lodge scarcity",
        points: [
          "Conservation rules keep jungle stays limited.",
          "Low supply supports premium, high-value positioning.",
          "Eco-friendly modular builds fit the reserve edge.",
        ],
      },
      {
        title: "Value & appreciation",
        points: [
          "Attractive entry price for a unique asset.",
          "Scenic corridor land appreciates steadily.",
          "Differentiated asset stands apart from hill-station villas.",
        ],
      },
    ],
    roi: "26%",
    startPrice: "₹54 L",
    focus: "Wildlife & Safari Stays",
  },
  {
    id: "kodaikanal",
    name: "Kodaikanal",
    region: "Western Ghats",
    state: "Tamil Nadu",
    tagline: "The Princess of Hill Stations",
    summary:
      "Set high in the Palani hills, Kodaikanal wraps a starry lake, pine-shrouded cliffs and cool mist in one of South India's most romantic settings. Its couples' market and boutique stay culture make it a perennial favourite.",
    image: "https://readdy.ai/api/search-image?query=Photorealistic%20view%20of%20Kodaikanal%20lake%20in%20Palani%20hills%20Tamil%20Nadu%2C%20serene%20star-shaped%20reservoir%20surrounded%20by%20pine%20forests%20and%20misty%20cliffs%2C%20peaceful%20mountain%20atmosphere%2C%20real%20landscape%20photography%2C%20ultra%20high%20detail%2C%20no%20buildings&width=1000&height=720&seq=dest-kodaikanal-09&orientation=landscape&nocache=true",
    highlights: [
      "Kodaikanal Lake & boating",
      "Coaker's Walk & Pillar Rocks",
      "Bryant Park & lush gardens",
      "Berijam Lake forest drive",
      "Pine forests & misty viewpoints",
      "Romantic couples' atmosphere",
    ],
    advantages: [
      "Strong honeymoon and couples travel demand year-round.",
      "Premium boutique and lake-view stays command high rates.",
      "Iconic, well-branded destination with steady tourist flow.",
      "Cool, misty climate fits cosy, romantic villa concepts.",
    ],
    climate: [
      { label: "Summer", value: "8°–20°C · Cool" },
      { label: "Winter", value: "5°–15°C · Cold" },
      { label: "Monsoon", value: "Oct–Dec · Moderate rain" },
      { label: "Best Time", value: "Oct–May" },
    ],
    climateSummary:
      "A cool, crisp highland climate with misty evenings and comfortable days — a romantic, year-round villa destination.",
    climateShort: "Cool & misty",
    elevation: "~2,133 m",
    peakSeason: "Oct–May",
    primaryMarket: "Honeymooners & couples",
    investment: [
      {
        title: "Couples' premium market",
        points: [
          "Honeymooners consistently pay a premium for private stays.",
          "Lake and cliff views justify higher nightly rates.",
          "Strong repeat and seasonal tourist flows.",
        ],
      },
      {
        title: "Iconic brand & visibility",
        points: [
          "Kodaikanal is a household-name hill station.",
          "Cinematic scenery drives organic marketing.",
          "Established resort and boutique-stay ecosystem.",
        ],
      },
      {
        title: "Scarcity & regulated build",
        points: [
          "Limited land near the lake and viewpoints.",
          "Eco-sensitivity supports long-term appreciation.",
          "Modular villas minimise disruption in sensitive zones.",
        ],
      },
    ],
    roi: "31%",
    startPrice: "₹88 L",
    focus: "Couples & Lake Retreats",
  },
];

export const DESTINATION_NAMES: string[] = DESTINATIONS.map((d) => d.name);

export interface ComparisonRowDef {
  label: string;
  key: keyof Destination;
}

export const COMPARISON_ROWS: ComparisonRowDef[] = [
  { label: "Elevation", key: "elevation" },
  { label: "Climate", key: "climateShort" },
  { label: "Peak Season", key: "peakSeason" },
  { label: "Primary Market", key: "primaryMarket" },
  { label: "Best Stay Type", key: "focus" },
  { label: "Est. ROI", key: "roi" },
  { label: "Entry Investment", key: "startPrice" },
];